import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Compass, ArrowRight, ShieldCheck, Store, HeartHandshake, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { CategoryGrid } from "@/components/CategoryGrid";
import { BusinessCard } from "@/components/BusinessCard";
import { Section } from "@/components/Section";
import { DealsStrip } from "@/components/DealsStrip";
import { CardsSkeleton } from "@/components/CardsSkeleton";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CitySelector } from "@/components/CitySelector";

const HERO = "https://images.unsplash.com/photo-1767340524873-f51b3c04ad38?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function Home() {
  useSeo("OyeBest — Apne Shehar Ki Best Jagah, Ek Jagah.", "Discover the best restaurants, hotels, shops, salons and local services in your city. OyeBest — Apne Shehar Ki Best Jagah, Ek Jagah.");
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("businesses");
  const [serviceCats, setServiceCats] = useState([]);
  const [city, setCity] = useState(localStorage.getItem("oyebest_city") || "etah");
  const [cityName, setCityName] = useState(localStorage.getItem("oyebest_city_name") || "Etah");

  useEffect(() => { api.get("/service-categories").then((r) => setServiceCats(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/cities"),
      api.get(`/city/${city}`),
    ]).then(([cats, cts, cityData]) => {
      setCategories(cats.data);
      setCities(cts.data);
      setFeatured(cityData.data.featured);
      setTopRated(cityData.data.top_rated);
      setOffers(cityData.data.offers || []);
    }).finally(() => setLoading(false));
  }, [city]);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(q)}&city=${city}&tab=${mode}`);
  };

  return (
    <>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Indian city street" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-sm font-semibold text-white border border-white/20">
              <MapPin className="w-4 h-4 text-primary" /> Now live in Etah, Uttar Pradesh
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
              Apne Shehar Ki <span className="text-primary">Best Jagah</span>, Ek Jagah.
            </h1>
            <p className="mt-5 text-lg text-white/85 max-w-2xl">
              Restaurants, Hotels, Shops, Services aur bahut kuch — apne shehar mein aasani se dhoondhiye.
            </p>

            <div className="mt-7 inline-flex rounded-full bg-white/15 border border-white/25 p-1" data-testid="hero-mode-toggle">
              <button type="button" onClick={() => setMode("businesses")} className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors ${mode === "businesses" ? "bg-white text-foreground" : "text-white"}`} data-testid="hero-mode-businesses">Businesses</button>
              <button type="button" onClick={() => setMode("services")} className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors ${mode === "services" ? "bg-white text-foreground" : "text-white"}`} data-testid="hero-mode-services">Services</button>
            </div>

            <form onSubmit={doSearch} className="mt-4 bg-white rounded-2xl p-2.5 flex flex-col sm:flex-row gap-2.5 shadow-2xl max-w-2xl">
              <div className="flex items-center flex-1 px-3">
                <Search className="w-5 h-5 text-muted-foreground shrink-0" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={mode === "services" ? "AC mechanic, Electrician, Plumber…" : "Restaurant, Hotel, Salon, Shoes…"}
                  className="w-full outline-none px-3 py-3 text-base"
                  data-testid="hero-search-input"
                />
              </div>
              <CitySelector
                valueSlug={city}
                valueName={cityName}
                onSelect={(slug, name) => { setCity(slug); setCityName(name); localStorage.setItem("oyebest_city", slug); localStorage.setItem("oyebest_city_name", name); }}
                triggerClassName="sm:w-44 h-12 rounded-xl border-border bg-white justify-between w-full"
              />
              <Button type="submit" className="h-12 rounded-xl px-8 font-semibold gap-2" data-testid="hero-search-btn">
                <Search className="w-4 h-4" /> Search
              </Button>
            </form>

            <div className="mt-4 flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => navigate(`/${city}`)} className="rounded-full bg-white/10 text-white border-white/30 hover:bg-white/20 gap-2 h-11" data-testid="explore-nearby-btn">
                <Compass className="w-4 h-4" /> Explore Nearby
              </Button>
              <Button variant="outline" onClick={() => navigate("/services")} className="rounded-full bg-white/10 text-white border-white/30 hover:bg-white/20 gap-2 h-11" data-testid="browse-services-btn">
                <Wrench className="w-4 h-4" /> Browse Services
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <div className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, t: "Verified Businesses", s: "Genuine, trusted local listings" },
            { icon: Store, t: "List for FREE", s: "Apna business list karein, muft mein" },
            { icon: HeartHandshake, t: "Direct Connect", s: "Call, WhatsApp ya Contact Now" },
          ].map((x) => (
            <div key={x.t} className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-xl bg-accent text-primary flex items-center justify-center shrink-0"><x.icon className="w-5 h-5" /></span>
              <div><p className="font-bold text-sm">{x.t}</p><p className="text-xs text-muted-foreground">{x.s}</p></div>
            </div>
          ))}
        </div>
      </div>

      {/* Today's Deals */}
      {offers.length > 0 && <DealsStrip offers={offers} />}

      {/* Categories */}
      <Section title="Popular Categories" subtitle="Har zaroorat ke liye, sahi jagah" id="categories"
        action={<Button variant="ghost" onClick={() => navigate("/explore")} className="rounded-full gap-1 font-semibold" data-testid="view-all-categories-btn">View All <ArrowRight className="w-4 h-4" /></Button>}>
        <CategoryGrid categories={categories} city={city} limit={15} />
      </Section>

      {/* Popular Services */}
      <div className="bg-secondary/50">
        <Section title="Popular Services" subtitle="Ghar baithe bulwaayein bharosemand service providers" id="services"
          action={<Button variant="ghost" onClick={() => navigate("/services")} className="rounded-full gap-1 font-semibold" data-testid="view-all-services-btn">View All <ArrowRight className="w-4 h-4" /></Button>}>
          <CategoryGrid categories={serviceCats} basePath={`/services/${city}`} limit={12} />
        </Section>
      </div>

      {/* Featured */}
      {(loading || featured.length > 0) && (
        <div className="bg-secondary/50">
          <Section title="Featured Businesses" subtitle="Top picks in your city"
            action={<Button variant="ghost" onClick={() => navigate(`/${city}`)} className="rounded-full gap-1 font-semibold">See all <ArrowRight className="w-4 h-4" /></Button>}>
            {loading ? <CardsSkeleton /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {featured.map((b, i) => <BusinessCard key={b.id} business={b} index={i} />)}
              </div>
            )}
          </Section>
        </div>
      )}

      {/* Top rated */}
      {(loading || topRated.length > 0) && (
        <Section title={`Top Rated in ${cityName}`} subtitle="Loved by the locals">
          {loading ? <CardsSkeleton /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {topRated.map((b, i) => <BusinessCard key={b.id} business={b} index={i} />)}
            </div>
          )}
        </Section>
      )}

      {/* Get Website CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl bg-[#0f1115] text-white overflow-hidden grid md:grid-cols-2">
          <div className="p-8 md:p-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">Apne Business Ko Online Grow Karein.</h2>
            <p className="mt-4 text-white/70">Professional business website with WhatsApp, Google Maps, menu, gallery & contact forms — SEO-friendly aur mobile-ready.</p>
            <Button onClick={() => navigate("/get-your-website")} className="mt-6 rounded-full h-12 px-8 font-semibold gap-2" data-testid="home-get-website-btn">
              Get Your Website <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="hidden md:block relative">
            <img src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1000&q=85" alt="Website design" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </>
  );
}
